import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

// Shape returned by the getuserpasswordlinkdata endpoint (passwordPolicy field).
export interface LinkPasswordPolicy {
  minLength: number;
  maxLength: number;
  uppercase: boolean;
  lowercase: boolean;
  number: boolean;
  special: boolean;
}

export interface PasswordRule {
  key: string;
  params?: Record<string, any>;
  test: (value: string) => boolean;
}

export const DEFAULT_LINK_PASSWORD_POLICY: LinkPasswordPolicy = {
  minLength: 6,
  maxLength: 64,
  uppercase: false,
  lowercase: false,
  number: false,
  special: false
};

// Keep in sync with validatePasswordAgainstPolicy in vsight-backend/functions-v2/src/helpers/passwordPolicy.ts
const COMMON_PASSWORDS = new Set('123456,pAssw0rd!,123456789,password,12345678,111111,123123,1234567890,1234567,qwerty,abc123,000000,password1,123321,666666,112233,123abc,123qwe,121212,abcd1234,123654,11111111,asdasd,asd123,222222,888888,7777777,1234qwer,password123,12341234,123456,101010,1111111,samsung,apple'.split(','));
const SPECIAL_CHAR_REGEX = /[-*/?#&()@%+'!$^:,~_.½£₺é;{}ß>|æ]/;

export function buildPasswordRules(policy: LinkPasswordPolicy): PasswordRule[] {
  const rules: PasswordRule[] = [
    { key: 'USER_PASSWORD.RULE_MIN_LENGTH', params: { min: policy.minLength }, test: v => v.length >= policy.minLength },
    { key: 'USER_PASSWORD.RULE_MAX_LENGTH', params: { max: policy.maxLength }, test: v => v.length > 0 && v.length <= policy.maxLength }
  ];
  if (policy.uppercase) {
    rules.push({ key: 'USER_PASSWORD.RULE_UPPERCASE', test: v => /[A-Z]/.test(v) });
  }
  if (policy.lowercase) {
    rules.push({ key: 'USER_PASSWORD.RULE_LOWERCASE', test: v => /[a-z]/.test(v) });
  }
  if (policy.number) {
    rules.push({ key: 'USER_PASSWORD.RULE_NUMBER', test: v => /[0-9]/.test(v) });
  }
  if (policy.special) {
    rules.push({ key: 'USER_PASSWORD.RULE_SPECIAL', test: v => SPECIAL_CHAR_REGEX.test(v) });
  }
  rules.push(
    { key: 'USER_PASSWORD.RULE_NO_SPACES', test: v => v.length > 0 && !/\s/.test(v) },
    { key: 'USER_PASSWORD.RULE_NOT_COMMON', test: v => v.length > 0 && !COMMON_PASSWORDS.has(v) }
  );
  return rules;
}

export function passwordRulesValidator(getRules: () => PasswordRule[]): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value || '';
    return getRules().every(rule => rule.test(value)) ? null : { passwordPolicy: true };
  };
}
